import React, { Component } from 'react';
import { connect } from 'react-redux';
import { InputText } from 'primereact/inputtext';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { RadioButton } from 'primereact/radiobutton';
import { Toast } from 'primereact/toast';
import { DATATABLE_CONFIG, USER_SUBMENU_ID } from '../../../shared/Config';
import { HASACCESS } from '../../../shared/utility';
import Messages from '../../../shared/UI/Messages/Messages';
import * as actions from '../../../store/actions/index';

// ── Dummy data ────────────────────────────────────────────────────────────────
const DUMMY_DATA = [
    { id: 1, rawMaterial: 'Ad-raw Poha',   unit: 'Kg',  invoiceDate: '14 Sep 2023', invoiceNumber: 'INV-001', expiryDate: '31 Aug 2023', actualQty: 111, price: 111 },
    { id: 2, rawMaterial: 'Wheat Flour',   unit: 'Kg',  invoiceDate: '10 Sep 2023', invoiceNumber: 'INV-002', expiryDate: '25 Sep 2023', actualQty: 50,  price: 40  },
    { id: 3, rawMaterial: 'Rice Bran Oil', unit: 'Ltr', invoiceDate: '05 Sep 2023', invoiceNumber: 'INV-003', expiryDate: '20 Oct 2023', actualQty: 30,  price: 120 },
    { id: 4, rawMaterial: 'Tomato Puree',  unit: 'Kg',  invoiceDate: '12 Sep 2023', invoiceNumber: 'INV-004', expiryDate: '28 Sep 2023', actualQty: 20,  price: 85  },
];

const initTableData = () =>
    DUMMY_DATA.map(row => ({ ...row, wastageQty: 0, amount: 0 }));

class ExpiryReport extends Component {
    state = {
        selectType:    'expired',
        selectDays:    null,
        tableData:     [],
        selectedItems: [],
        searched:      false,
        globalFilter:  null,
    };

    componentDidMount() {
        this.getAccess = HASACCESS(USER_SUBMENU_ID);
    }

    handleSearch = () => {
        this.setState({ tableData: initTableData(), searched: true, selectedItems: [] });
    };

    handleExport = () => {
        this.growl.show({ severity: 'info', summary: 'Export', detail: 'Export triggered' });
    };

    // ── Validation + navigate to next page ───────────────────────────────────
    handleAddToWastage = () => {
        const { selectedItems } = this.state;

        // Rule 1: at least one row must be selected
        if (!selectedItems || selectedItems.length === 0) {
            this.growl.show({ severity: 'warn', summary: 'Validation', detail: 'Please select at least one row.' });
            return;
        }

        // Rule 2: all selected rows must have wastage qty > 0
        const zeroQtyRows = selectedItems.filter(r => !r.wastageQty || r.wastageQty <= 0);
        if (zeroQtyRows.length > 0) {
            this.growl.show({
                severity: 'warn',
                summary: 'Validation',
                detail: `Wastage quantity must be > 0 for: ${zeroQtyRows.map(r => r.rawMaterial).join(', ')}`,
            });
            return;
        }

        // Pass only selected rows to the next page via router state
        this.props.history.push('/wastage-report/add', { wastageItems: selectedItems });
    };

    // ── Delete a row ──────────────────────────────────────────────────────────
    handleDeleteRow = (rowId) => {
        this.setState(prev => ({
            tableData:     prev.tableData.filter(r => r.id !== rowId),
            selectedItems: prev.selectedItems.filter(r => r.id !== rowId),
        }));
    };

    // ── Update wastage qty — auto-cap at actualQty (Rule: cannot exceed actual) ──
    handleWastageQtyChange = (rowId, value) => {
        this.setState(prev => ({
            tableData: prev.tableData.map(row => {
                if (row.id !== rowId) return row;
                let qty = parseFloat(value) || 0;
                if (qty > row.actualQty) qty = row.actualQty; // hard cap
                return { ...row, wastageQty: qty, amount: qty * row.price };
            }),
            // keep selectedItems in sync with updated amounts
            selectedItems: prev.selectedItems.map(row => {
                if (row.id !== rowId) return row;
                let qty = parseFloat(value) || 0;
                if (qty > row.actualQty) qty = row.actualQty;
                return { ...row, wastageQty: qty, amount: qty * row.price };
            }),
        }));
    };

    // ── Computed total wastage ────────────────────────────────────────────────
    getTotalWastage = () =>
        this.state.tableData.reduce((sum, row) => sum + (row.amount || 0), 0);

    // ── Column templates ──────────────────────────────────────────────────────

    amountTemplate = (rowData) => (
        <span>{rowData.amount.toLocaleString('en-IN')}</span>
    );

    // Editable input — capped to actualQty via onChange
    wastageQtyTemplate = (rowData) => (
        <InputText
            value={rowData.wastageQty === 0 ? '' : rowData.wastageQty}
            placeholder={`Max: ${rowData.actualQty}`}
            style={{ width: '110px' }}
            keyfilter="num"
            onChange={(e) => this.handleWastageQtyChange(rowData.id, e.target.value)}
        />
    );

    deleteTemplate = (rowData) => (
        <Button
            icon="pi pi-trash"
            className="p-button-danger p-button-text"
            onClick={() => this.handleDeleteRow(rowData.id)}
            tooltip="Remove row"
        />
    );

    render() {
        if (this.getAccess !== undefined && this.getAccess.viewAccess === false) {
            this.props.history.push('/dashboard');
        }

        const { tableData, searched, selectType, selectDays, selectedItems } = this.state;
        const isExpired    = selectType === 'expired';
        const totalWastage = this.getTotalWastage();

        const header = (
            <div style={{ textAlign: 'right' }}>
                <InputText
                    type="search"
                    onInput={(e) => this.setState({ globalFilter: e.target.value })}
                    placeholder="Search..."
                />
            </div>
        );

        // ── Total Wastage bar (read-only, no button) ──────────────────────────
        const WastageTotal = () => (
            <div className="p-d-flex p-jc-end p-ai-center">
                <span style={{ fontSize: '16px', fontWeight: 600, color: '#333' }}>
                    Total Wastage Amount :&nbsp;
                    <span style={{ color: '#e11d48' }}>
                        ₹ {totalWastage.toLocaleString('en-IN')}
                    </span>
                </span>
            </div>
        );

        // ── Bottom action bar: total first, then Add To Wastage below ─────────
        const BottomActionBar = () => (
            <div className="p-d-flex p-flex-column p-ai-end p-gap-2 p-mt-3" style={{ gap: '10px' }}>
                <WastageTotal />
                {isExpired && (
                    <Button
                        label="Add To Wastage"
                        className="p-button-secondary"
                        onClick={this.handleAddToWastage}
                    />
                )}
            </div>
        );

        return (
            <div className="p-grid padding">
                {this.props.loading  && <div className="page-loader"></div>}
                {this.props.error   !== null && <Messages type="error"   message={this.props.error} />}
                {this.props.success !== null && <Messages type="success" message={this.props.success} />}
                <Toast ref={(el) => this.growl = el} />

                <div className="p-col-12">
                    <div className="card card-w-title" style={{ position: 'relative' }}>
                        <h1>Expiry Report</h1>

                        {/* Export — top right */}
                        {/* <div className="p-d-flex p-jc-end p-mb-3">
                            <Button
                                label="Export"
                                icon="pi pi-file-excel"
                                className="p-button-warning"
                                onClick={this.handleExport}
                            />
                        </div> */}

                        {/* ── Filter Row ────────────────────────────────────── */}
                        <div className="p-grid p-align-end">

                            <div className="p-col-12 p-md-4 p-lg-4 p-d-flex p-flex-column p-gap-2">
                                <label className="label">Select Type</label>
                                <div className="p-d-flex p-ai-center" style={{ gap: '20px' }}>
                                    {[{ label: 'Expired', value: 'expired' }, { label: 'About to expire', value: 'about_to_expire' }].map(opt => (
                                        <div key={opt.value} className="p-d-flex p-ai-center" style={{ gap: '6px' }}>
                                            <RadioButton
                                                inputId={`type_${opt.value}`}
                                                value={opt.value}
                                                onChange={(e) => this.setState({ selectType: e.value, searched: false, tableData: [], selectedItems: [] })}
                                                checked={selectType === opt.value}
                                            />
                                            <label htmlFor={`type_${opt.value}`} style={{ cursor: 'pointer' }}>{opt.label}</label>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <div className="p-col-12 p-md-4 p-lg-4 p-d-flex p-flex-column p-gap-2">
                                <label className="label">Select Days</label>
                                <div className="p-d-flex p-ai-center" style={{ gap: '20px' }}>
                                    {[30, 60, 90].map(day => (
                                        <div key={day} className="p-d-flex p-ai-center" style={{ gap: '6px' }}>
                                            <RadioButton
                                                inputId={`day_${day}`}
                                                value={day}
                                                onChange={(e) => this.setState({ selectDays: e.value })}
                                                checked={selectDays === day}
                                            />
                                            <label htmlFor={`day_${day}`} style={{ cursor: 'pointer' }}>{day}</label>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <div className="p-col-12 p-md-4 p-lg-4 p-d-flex p-ai-end">
                                <Button
                                    label="Search"
                                    className="p-button-primary"
                                    onClick={this.handleSearch}
                                />
                            </div>

                        </div>
                        {/* ── End Filter Row ────────────────────────────────── */}

                        {/* ── Table section ───────────────────────────────── */}
                        {searched && (
                            <div className="p-col-12" style={{ marginTop: '1.5em' }}>

                                {/* Total only above table (no button here) */}
                                <div className="p-mb-2"><WastageTotal /></div>

                                {/* <DataTable
                                    value={tableData}
                                    globalFilter={this.state.globalFilter}
                                    header={header}
                                    selection={isExpired ? selectedItems : undefined}
                                    onSelectionChange={isExpired ? (e) => this.setState({ selectedItems: e.value }) : undefined}
                                    {...DATATABLE_CONFIG}
                                >
                                    {isExpired && (
                                        <Column selectionMode="multiple" style={{ width: '3em' }} />
                                    )}
                                    <Column field="rawMaterial"   header="Raw Material"    sortable style={{ minWidth: '160px' }} />
                                    <Column field="unit"          header="Unit"             style={{ textAlign: 'center' }} />
                                    <Column field="invoiceDate"   header="Invoice Date"     sortable style={{ textAlign: 'center' }} />
                                    <Column field="invoiceNumber" header="Invoice Number"   style={{ textAlign: 'center' }} />
                                    <Column field="expiryDate"    header="Expiry Date"      sortable style={{ textAlign: 'center' }} />
                                    <Column field="actualQty"     header="Actual Quantity"  sortable style={{ textAlign: 'center' }} />
                                    <Column field="price"         header="Price (₹)"        sortable style={{ textAlign: 'right' }} />

                                    {isExpired ? (
                                        <Column
                                            header="Wastage Quantity"
                                            body={this.wastageQtyTemplate}
                                            style={{ textAlign: 'center', minWidth: '150px' }}
                                        />
                                    ) : (
                                        <Column field="actualQty" header="Wastage Quantity" style={{ textAlign: 'center' }} />
                                    )}

                                    <Column
                                         header={
                                    <div style={{ textAlign: 'center' }}>
                                        <div>Amount (₹)</div>
                                        <div style={{ fontSize: '11px', color: '#666',fontWeight: 'normal', marginTop: '2px' }} >
                                            (Quantity × Price)
                                        </div>
                                    </div>
                                }
                                        body={this.amountTemplate}
                                        style={{ textAlign: 'right', minWidth: '200px' }}
                                    />

                                    {isExpired && (
                                        <Column
                                            body={this.deleteTemplate}
                                            style={{ textAlign: 'center', width: '4em' }}
                                        />
                                    )}
                                </DataTable> */}

                                <DataTable
                                    value={tableData}
                                    globalFilter={this.state.globalFilter}
                                    header={header}
                                    selection={isExpired ? selectedItems : undefined}
                                    onSelectionChange={isExpired ? (e) => this.setState({ selectedItems: e.value }) : undefined}
                                    scrollable
                                    scrollDirection="horizontal"
                                    {...DATATABLE_CONFIG}
                                >
                                    {isExpired && (
                                        <Column selectionMode="multiple" style={{ width: '3em', flex: '0 0 3em' }} />
                                    )}
                                    <Column field="rawMaterial"   header="Raw Material"   sortable style={{ minWidth: '160px' }} />
                                    <Column field="unit"          header="Unit"                    style={{ minWidth: '80px',  textAlign: 'center' }} />
                                    <Column field="invoiceDate"   header="Invoice Date"   sortable style={{ minWidth: '130px', textAlign: 'center' }} />
                                    <Column field="invoiceNumber" header="Invoice Number"          style={{ minWidth: '140px', textAlign: 'center' }} />
                                    <Column field="expiryDate"    header="Expiry Date"    sortable style={{ minWidth: '130px', textAlign: 'center' }} />
                                    <Column field="actualQty"     header="Actual Quantity" sortable style={{ minWidth: '130px', textAlign: 'center' }} />
                                    <Column field="price"         header="Price (₹)"      sortable style={{ minWidth: '100px', textAlign: 'right' }} />

                                    {isExpired ? (
                                        <Column
                                            header="Wastage Quantity"
                                            body={this.wastageQtyTemplate}
                                            style={{ minWidth: '170px', textAlign: 'center' }}
                                        />
                                    ) : (
                                        <Column field="actualQty" header="Wastage Quantity" style={{ minWidth: '140px', textAlign: 'center' }} />
                                    )}

                                    <Column
                                        header={
                                            <div>
                                                <div>Amount (₹)</div>
                                                <div style={{ fontSize: '11px', color: '#666', fontWeight: 'normal', marginTop: '2px' }}>
                                                    (Quantity × Price)
                                                </div>
                                            </div>
                                        }
                                        body={this.amountTemplate}
                                        style={{ minWidth: '160px', textAlign: 'right' }}
                                    />

                                    {isExpired && (
                                        <Column
                                            body={this.deleteTemplate}
                                            style={{ width: '60px', textAlign: 'center', flex: '0 0 60px' }}
                                        />
                                    )}
                                </DataTable>
                                {/* Total + Add To Wastage button stacked at bottom right */}
                                <BottomActionBar />

                            </div>
                        )}

                        {!searched && (
                            <div
                                className="p-d-flex p-jc-center p-ai-center"
                                style={{ minHeight: '200px', color: '#aaa', fontSize: '15px' }}
                            >
                                <i className="pi pi-search p-mr-2" />
                                Select type and days, then click Search to view the report.
                            </div>
                        )}

                    </div>
                </div>
            </div>
        );
    }
}

const mapStateToProps = state => ({
    loggedUserDetail: state.auth.userObject,
    loading:          state.master.loading,
    error:            state.master.error,
    success:          state.master.success,
    tableLists:       state.master.tableLists,
});

const mapDispatchToProps = dispatch => ({
    dynamicDeleteProcess: (model, data, func) => dispatch(actions.dynamicDeleteProcess(model, data, func)),
    getMasterTableList:   (table)             => dispatch(actions.getMasterTableList(table)),
});

export default connect(mapStateToProps, mapDispatchToProps)(ExpiryReport);